import type {
  ExternalLoggerTelemetryEvent,
  PXNovaTelemetryEvent,
} from "@1js/midgard-types";
import { pxNovaTelemetryEvent } from "@1js/px-nova-events";
import type { NovaEvent } from "@nova/types";
import type {
  DismissEvent,
  HostAppContext,
  LivePersonaEditorEvent,
  ManageSourcesEvent,
  MODES,
  RenderCompleteEvent,
  RenderErrorEvent,
  RenderEvent,
  SaveContactFailureEvent,
  SaveContactSuccessEvent,
} from "./types";
import { originator } from "./types";

export const events = {
  /**
   *
   * Fired when manage sources is clicked from persona selector
   */
  onOpenManageSources: (): ManageSourcesEvent => ({
    originator,
    type: "onOpenManageSources",
  }),

  /**
   * Fired when form is dismissed.
   */
  onDismiss: (hostAppContext: HostAppContext): DismissEvent => ({
    originator,
    type: "onDismiss",
    data: () => hostAppContext,
  }),

  /**
   * Fired when form is rendered with data.
   * @param timestamp
   */
  onRenderComplete: (timestamp: number): RenderCompleteEvent => ({
    originator,
    type: "onRenderComplete",
    data: () => timestamp,
  }),

  /**
   * Fired when form is launched with/without data.
   * @param timestamp
   */
  onRender: (timestamp: number): RenderEvent => ({
    originator,
    type: "onRender",
    data: () => timestamp,
  }),

  /**
   * Fired when form is rendered with data.
   * @param timestamp
   */
  onRenderError: (timestamp: number): RenderErrorEvent => ({
    originator,
    type: "onRenderError",
    data: () => timestamp,
  }),

  /**
   * Fired when a contact is saved successfully.
   * @param contactId
   * @param successMessage
   * @param canDismiss
   */
  onSaveContactSuccess: (
    contactId: string,
    successMessage: string,
    hostAppContext: HostAppContext,
    mode: MODES,
    canDismiss: boolean = true
  ): SaveContactSuccessEvent => ({
    originator,
    type: "onSaveContactSuccess",
    data: () => ({
      contactId,
      successMessage,
      hostAppContext,
      mode,
      canDismiss,
    }),
  }),

  /**
   * Fired when a contact fails to save.
   * @param errorCode
   * @param errorMessage
   * @param canDismiss
   */
  onSaveContactFailure: (
    errorCode: string,
    errorMessage: string,
    hostAppContext: HostAppContext,
    canDismiss: boolean = true
  ): SaveContactFailureEvent => ({
    originator,
    type: "onSaveContactFailure",
    data: () => ({
      errorCode,
      errorMessage,
      hostAppContext,
      canDismiss,
    }),
  }),

  /**
   * Fired to track PX nova telemetry events.
   * @param telemetryEvent
   * @returns
   */
  pxNovaTelemetryEvent: (
    telemetryEvent: ExternalLoggerTelemetryEvent
  ): PXNovaTelemetryEvent => pxNovaTelemetryEvent(originator, telemetryEvent),
} as const;

export const isLivePersonaEditorEvent = (
  event: NovaEvent<unknown>
): event is LivePersonaEditorEvent => "LivePersonaEditor" === event.originator;
